import type {
  AudienceForm,
  AudienceListType,
  GroupFieldName,
  GroupIndexFieldName,
  GroupLike,
  GroupsPath,
  IndexedGroupFieldName,
} from '../../../types';

type ParsedIndexedGroupFieldName = {
  listType: AudienceListType;
  groupIndex: number;
  subGroupIndex?: number;
};

type AudienceListFieldName = `normalized_definition.${AudienceListType}`;
type IndexedGroupFieldRoot = GroupsPath | `${IndexedGroupFieldName}.groups`;

const GROUP_PATH_ROOT = 'normalized_definition';
const GROUP_PATH_SEGMENT = 'groups';

const isAudienceListType = (value: string): value is AudienceListType => value === 'includes' || value === 'excludes';

const parseIndexSegment = (value: string | undefined): number | null => {
  if (value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);

  if (!Number.isInteger(parsedValue) || parsedValue < 0 || String(parsedValue) !== value) {
    return null;
  }

  return parsedValue;
};

// Keep path parsing in one place so type guards and group lookup stay aligned when the path shape changes.
export const parseIndexedGroupFieldName = (path: GroupFieldName): ParsedIndexedGroupFieldName | null => {
  const [root, listType, groupSegment, groupIndexSegment, nestedGroupSegment, subGroupIndexSegment, ...rest] =
    path.split('.');

  if (
    rest.length > 0 ||
    root !== GROUP_PATH_ROOT ||
    !isAudienceListType(listType) ||
    groupSegment !== GROUP_PATH_SEGMENT
  ) {
    return null;
  }

  const groupIndex = parseIndexSegment(groupIndexSegment);

  if (groupIndex === null) {
    return null;
  }

  if (nestedGroupSegment === undefined && subGroupIndexSegment === undefined) {
    return { listType, groupIndex };
  }

  if (nestedGroupSegment !== GROUP_PATH_SEGMENT) {
    return null;
  }

  const subGroupIndex = parseIndexSegment(subGroupIndexSegment);

  if (subGroupIndex === null) {
    return null;
  }

  return { listType, groupIndex, subGroupIndex };
};

export const isIndexedGroupFieldName = (path: GroupFieldName): path is IndexedGroupFieldName => {
  return parseIndexedGroupFieldName(path) !== null;
};

export const buildGroupsPath = (listPath: AudienceListFieldName): GroupsPath => {
  return `${listPath}.groups` as GroupsPath;
};

// Build indexed paths through a helper so render/hooks code does not duplicate the string structure.
export function buildIndexedGroupFieldName(path: GroupsPath, index: number): GroupIndexFieldName;
export function buildIndexedGroupFieldName(
  path: `${IndexedGroupFieldName}.groups`,
  index: number
): IndexedGroupFieldName;
export function buildIndexedGroupFieldName(path: IndexedGroupFieldRoot, index: number): IndexedGroupFieldName {
  return `${path}.${index}` as IndexedGroupFieldName;
}

export const getGroupFromPath = (
  definition: AudienceForm['normalized_definition'] | undefined,
  path: GroupFieldName
): GroupLike | undefined => {
  // Resolve the path once, then reuse the parsed indices so lookup and validation cannot drift apart.
  const parsedPath = parseIndexedGroupFieldName(path);

  if (!parsedPath) return undefined;

  const { listType, groupIndex, subGroupIndex } = parsedPath;

  const topGroup = definition?.[listType].groups[groupIndex];

  if (subGroupIndex === undefined) return topGroup;
  if (!topGroup || !('groups' in topGroup)) return undefined;

  return topGroup.groups[subGroupIndex];
};
